import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Browser } from '@capacitor/browser';
import { NavController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Observable, catchError, switchMap, take, throwError } from 'rxjs';
import { AuthData, SignUpError, SignupReq } from 'src/app/api/contracts/auth';
import { DeliveryLocation } from 'src/app/api/contracts/location';
import { AuthService } from 'src/app/auth/auth.service';
import { AppDropdownModel } from 'src/app/components/UI/dropdown/dropdown.types';
import { APP_CONFIG, AppConfig } from 'src/app/config/app-config';
import { AppEventsService } from 'src/app/services/app-events.service';
import { LanguageService } from 'src/app/services/language.service';
import { LocationService } from 'src/app/services/location.service';
import { ModalService } from 'src/app/services/modal.service';
import { StorageKeys, StorageService } from 'src/app/services/storage.service';
import { GenericModalType } from 'src/app/types/modals';
import { LoaderController } from 'src/app/utils/loader-controller';
import { confirmPasswordFormValidator } from 'src/app/validators/confirm-password.validator';

@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./../auth-common.scss', './signup.component.scss'],
})
export class SignupComponent implements OnInit {
  readonly signupForm: FormGroup<Record<keyof SignupReq, FormControl>>;
  readonly termsOfUseAgreementControl: FormControl<boolean | null> =
    new FormControl<boolean>(false);
  public readonly loader = new LoaderController();

  locationDropdownModel: AppDropdownModel = {
    options: [],
  };

  shouldClearErrorsAfterSubmit: boolean = false;

  constructor(
    @Inject(APP_CONFIG) private readonly appConfig: AppConfig,
    private readonly navCtrl: NavController,
    private readonly authService: AuthService,
    private readonly eventService: AppEventsService,
    private readonly storageService: StorageService,
    private readonly locationService: LocationService,
    private readonly languageService: LanguageService,
    private readonly modalService: ModalService,
    private readonly translateService: TranslateService
  ) {
    this.signupForm = this.createForm();
    this.signupForm.setValidators(
      confirmPasswordFormValidator('password', 'confirmPassword')
    );
  }

  ngOnInit() {
    this.initLocationDropdownModelOptions();

    this.signupForm.controls.email.valueChanges.subscribe(() => {
      if (this.shouldClearErrorsAfterSubmit) {
        this.signupForm.controls.email.setErrors(null);
        this.shouldClearErrorsAfterSubmit = false;
      }
    });

    this.signupForm.controls.pickupHandle.valueChanges.subscribe(() => {
      if (this.shouldClearErrorsAfterSubmit) {
        this.signupForm.controls.pickupHandle.setErrors(null);
        this.shouldClearErrorsAfterSubmit = false;
      }
    });
  }

  onSignupButtonClick(): void {
    if (this.loader.isLoadingNow()) {
      return;
    }
    this.loader.enqueue();
    this.shouldClearErrorsAfterSubmit = true;
    this.authService
      .signup(this.signupForm.value as SignupReq)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          const errorBody = err.error as SignUpError;
          if (errorBody.violations.find((v) => v.propertyPath === 'email')) {
            this.signupForm.controls.email.setErrors({
              custom: {
                existing: 'email is taken',
              },
            });
          }
          if (
            errorBody.violations.find((v) => v.propertyPath === 'pickupHandle')
          ) {
            this.signupForm.controls.pickupHandle.setErrors({
              custom: {
                existing: 'username is taken',
              },
            });
          }
          return throwError(() => err);
        }),
        switchMap(() =>
          this.modalService.showGenericInfoPopup({
            type: GenericModalType.SUCCESS,
            header: this.translateService.instant(
              '@pages.signup.successModal.header'
            ),
            message: this.translateService.instant(
              '@pages.signup.successModal.message'
            ),
          })
        )
      )
      .subscribe({
        next: () => {
          this.navCtrl.navigateRoot('login');
        },
        error: (err) => {
          this.loader.dequeue();
        },
        complete: () => {
          this.loader.dequeue();
        },
      });
  }

  onAlreadyHaveAccountClick(): void {
    this.navCtrl.navigateRoot('login');
  }

  private createForm(): FormGroup {
    return new FormGroup({
      name: new FormControl('', [
        Validators.required,
        // Validators.minLength(this.appConfig.fieldValidation.fullname.minLength),
        Validators.maxLength(64),
        Validators.pattern(this.appConfig.fieldValidation.fullname.pattern),
      ]),
      email: new FormControl('', [
        Validators.required,
        Validators.maxLength(64),
        Validators.pattern(this.appConfig.fieldValidation.email.pattern),
      ]),
      pickupHandle: new FormControl('', [
        Validators.required,
        Validators.minLength(this.appConfig.fieldValidation.username.minLength),
        Validators.maxLength(64),
        Validators.pattern(this.appConfig.fieldValidation.username.pattern),
      ]),
      password: new FormControl('', [
        Validators.required,
        Validators.minLength(this.appConfig.fieldValidation.password.minLength),
        Validators.maxLength(64),
        Validators.pattern(this.appConfig.fieldValidation.password.pattern),
      ]),
      confirmPassword: new FormControl('', [
        Validators.required,
        Validators.minLength(this.appConfig.fieldValidation.password.minLength),
        Validators.maxLength(64),
        Validators.pattern(this.appConfig.fieldValidation.password.pattern),
      ]),
      locationHash: new FormControl(null, [Validators.required]),
      registrationCompanyCode: new FormControl(null, [
        Validators.maxLength(64),
        Validators.minLength(1),
      ]),
    });
  }

  onOpenPolicyLink(): void {
    const currentLang = this.languageService.currentLang
      .pipe(take(1))
      .subscribe((currentLang) => {
        Browser.open({
          url: this.appConfig.privacyPolicyUrls[currentLang],
        });
      });
  }

  onOpenTermsOfUseLink(): void {
    this.navCtrl.navigateForward('terms-of-use');
  }

  onOpenImprintLink(): void {
    this.navCtrl.navigateForward('imprint');
  }

  private initLocationDropdownModelOptions(): void {
    this.locationService.retrieveLocations().subscribe((locations) => {
      this.locationDropdownModel.options = locations.map((location) => {
        return {
          value: location.hash,
          label: location.name,
        };
      });
    });
  }
}
